import { useEffect, useState } from 'react';
import {
    BasicResponsiveImage,
    Box,
    Button,
    Column,
    GridContainer,
    H2,
    P,
    ProgressiveImage,
    Row,
    StandardTile,
    StandardTileContents,
    StandardTileImage,
    StandardTileName,
    Typography,
} from '@vp/swan';
import { useLogger, useStyles } from '@vp/ubik-context';
import { useTrack } from '../../../../utils/analytics';
import { useEnvContext } from '../../../../utils/environmentContext';
import { toSentenceCase, toTitleCase } from '../../../../utils/helper';
import { getDesignableProductPrice, priceFromFloat } from '../../../../utils/pricing';
import { logoInspirationBasePath, logoInspirationKeywordPath } from '../../../../utils/urls';
import ImageDetailsModal from '../ImageDetailsModal/ImageDetailsModal';
import styles, { stylesheet } from './ImageTiles.module.scss';
import type { Logger } from '@vp/ubik-logging';
import type { Design } from '../../content/logo-inspiration';

const PAGE_SIZE = 12;

interface ImageTilesProps {
    designs: Design[];
    keyword?: string;
    keywordLongName?: string;
}

const ImageTiles = ({ designs, keyword, keywordLongName }: ImageTilesProps) => {
    useStyles(stylesheet);

    const [selectedDesign, setSelectedDesign] = useState<Design | null>(null);
    // We initialise the UI with the first 2 pages of designs and paginate the rest (PAGE_SIZE per page)
    const [shownDesigns, setShownDesigns] = useState<Design[]>(designs.slice(0, PAGE_SIZE * 2));
    // Hence, we initialise the page to 2
    const [page, setPage] = useState(2);
    const [logoPrice, setLogoPrice] = useState<string>('');
    const track = useTrack();
    const logger: Logger = useLogger();
    const { ENV_API_BASE_URL } = useEnvContext();

    useEffect(() => {
        const fetchPrice = async () => {
            const price = await getDesignableProductPrice(
                'logoessential',
                'en-US',
                ENV_API_BASE_URL,
                logger,
            );
            if (price) {
                const formattedPrice = priceFromFloat(price.list_price.taxed, 'en-US');
                setLogoPrice(formattedPrice);
            }
        };

        fetchPrice();
    }, []);

    const preloadModalImage = (imageSrc: string) => {
        const image = new Image();
        image.src = imageSrc;
    };

    async function handleShowMoreClick() {
        setPage(page + 1);
        setShownDesigns(designs.slice(0, (page + 1) * PAGE_SIZE));

        track('Navigation Clicked', {
            pageName: keyword
                ? `logoInspiration:${toTitleCase(keyword)} Logos Page`
                : 'logoInspiration:Root Logos Page',
            pageSection: 'Image Tiles',
            category: 'Logo Ideas',
            label: 'Content:Image Tiles',
            eventDetail: keyword
                ? `${logoInspirationKeywordPath(keyword)};${logoInspirationKeywordPath(keyword)};Image Tiles;showMore`
                : `${logoInspirationBasePath};${logoInspirationBasePath}/;Image Tiles;showMore`,
            navigationDetail: 'showMore',
        });
    }

    return (
        <>
            <ImageDetailsModal design={selectedDesign} onDismiss={() => setSelectedDesign(null)} />
            <Box paddingLeft={{ xs: 3, sm: 3, md: 4, lg: 4, xl: 4 }}>
                <H2 fontSkin="title-section">{`Hand-crafted ${keywordLongName ? keywordLongName + ' ' : ''}logo inspiration`}</H2>
                <Typography fontSkin="body-standard" marginBottom="between-subsections">
                    {`These ${keywordLongName ? keywordLongName + ' ' : ''}logos were crafted from scratch by professional designers from our global community. You too can work with a freelancer or explore dozens of ideas to get your dream logo`}
                    {logoPrice && ` Prices start at ${logoPrice}.`}
                </Typography>
            </Box>
            <GridContainer>
                <Row>
                    {shownDesigns.map((design: Design) => (
                        <Column
                            key={design.id}
                            span={4}
                            spanXl={3}
                            spanLg={3}
                            spanMd={4}
                            spanSm={6}
                            spanXs={12}
                            marginBottom={6}
                        >
                            <script
                                type="application/ld+json"
                                dangerouslySetInnerHTML={{
                                    __html: JSON.stringify({
                                        '@context': 'https://schema.org',
                                        '@type': 'ImageObject',
                                        contentUrl: design.publicCroppedUrl,
                                        name: design.title,
                                        description: design.description,
                                        creator: {
                                            '@type': 'Person',
                                            name: design.designer.displayName,
                                        },
                                        sourceOrganization: {
                                            '@type': 'Organization',
                                            name: 'VistaPrint',
                                        },
                                    }),
                                }}
                            />
                            <Button
                                className={styles.tileButton}
                                onClick={() => setSelectedDesign(design)}
                                onMouseOver={() => preloadModalImage(design.publicUrl)}
                                width="full-width"
                                skin="unstyled"
                            >
                                <StandardTile skin="product" marginBottom={0}>
                                    <StandardTileImage>
                                        <ProgressiveImage
                                            src={design.publicCroppedUrl}
                                            trigger="inView"
                                            intersectionOptions={{ threshold: 0 }}
                                        >
                                            {(image, isLoading) => {
                                                return (
                                                    <BasicResponsiveImage
                                                        aspectRatio={1}
                                                        src={image.src}
                                                        alt={`${keywordLongName ? toSentenceCase(`${keywordLongName} logo`) : 'Logo'} with the title '${design.title}'`}
                                                        style={{
                                                            filter: isLoading
                                                                ? 'blur(20px)'
                                                                : 'none',
                                                            transition: 'filter 0.6s ease-out',
                                                        }}
                                                    />
                                                );
                                            }}
                                        </ProgressiveImage>
                                    </StandardTileImage>
                                    <StandardTileContents>
                                        <StandardTileName>
                                            <Typography fontSkin="body-small">
                                                by {design.designer.displayName}
                                            </Typography>
                                        </StandardTileName>
                                    </StandardTileContents>
                                </StandardTile>
                            </Button>
                        </Column>
                    ))}
                </Row>
                <Row>
                    <Column span={12} marginTop="to-actions" textAlign="center">
                        {shownDesigns.length === designs.length ? (
                            <P>
                                There are no further results for{' '}
                                {keywordLongName ? `${keywordLongName} logos` : 'logo ideas'}.
                                <br />
                                Please try a different category from the list below.
                            </P>
                        ) : (
                            <Button onClick={handleShowMoreClick}>
                                Show more{' '}
                                {keywordLongName ? `${keywordLongName} logo ideas` : 'logo ideas'}
                            </Button>
                        )}
                    </Column>
                </Row>
            </GridContainer>
        </>
    );
};

export default ImageTiles;
